import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { NetworkService } from '../services/network.service';
import { environment } from '@env/environment';

/**
 * Functional HTTP interceptor that:
 * 1. Attaches the Cognito ID token to outgoing API requests
 * 2. On 401 response: attempts token refresh and retries once
 * 3. If refresh fails: delegates sign-out decision to AuthService (resume-safe)
 * 4. OFFLINE-SAFE: When offline, throws a network error immediately
 *    instead of attempting token fetch (which would fail and trigger logout)
 *
 * RESUME-SAFE: This interceptor NEVER navigates to /login directly.
 * The AuthService handles sign-out decisions internally, respecting the
 * resume grace period to avoid signing out when Keychain/Keystore is
 * temporarily inaccessible after screen unlock.
 *
 * Only applies to requests targeting the FSM backend API.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const network = inject(NetworkService);

  // Only intercept our API requests (api primario + api2). api2 usa un dominio
  // distinto (api2.sifagent.co) para los endpoints nuevos (inspection-points),
  // así que el token debe adjuntarse también allí. @see steering 36.
  const targetsApi =
    req.url.startsWith(environment.apiUrl) ||
    req.url.startsWith(environment.apiV2Url);
  if (!targetsApi) {
    return next(req);
  }

  // OFFLINE-SAFE: If device is offline, don't try to fetch a token or make the request.
  // Throw a status-0 error immediately — the errorInterceptor will handle it silently,
  // and the offline layer (SQLite/SyncQueue) serves cached data.
  if (!network.isOnline()) {
    return throwError(() => new HttpErrorResponse({
      status: 0,
      statusText: 'Offline',
      url: req.url,
      error: new ProgressEvent('error'),
    }));
  }

  // Diagnostic: log sync queue requests passing through the interceptor
  const isSyncQueue = req.headers.has('X-Sync-Queue');
  if (isSyncQueue) {
    console.info(`[AuthInterceptor] Sync queue request: ${req.method} ${req.url}`);
  }

  return from(authService.getIdToken()).pipe(
    switchMap((token) => {
      if (!token) {
        if (isSyncQueue) {
          console.warn(`[AuthInterceptor] NO TOKEN for sync queue request: ${req.method} ${req.url}`);
        }
        // Check again if we went offline during the getIdToken() call
        if (!network.isOnline()) {
          return throwError(() => new HttpErrorResponse({
            status: 0,
            statusText: 'Offline',
            url: req.url,
            error: new ProgressEvent('error'),
          }));
        }

        // No token while online — check if user is still authenticated (resume grace)
        if (authService.isAuthenticated()) {
          // User is authenticated but token temporarily unavailable (resume grace period).
          // Throw a transient error — the request can be retried later.
          return throwError(() => new HttpErrorResponse({
            status: 0,
            statusText: 'TokenTemporarilyUnavailable',
            url: req.url,
            error: new ProgressEvent('error'),
          }));
        }

        // Truly no token and no session — user was signed out by AuthService
        return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }));
      }

      if (isSyncQueue) {
        console.info(`[AuthInterceptor] Token obtained, sending: ${req.method} ${req.url}`, { tokenLength: token.length });
      }

      const authReq = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });

      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // Network error during request — don't attempt refresh if offline
          if (error.status === 0 && !network.isOnline()) {
            return throwError(() => error);
          }

          if (error.status === 401) {
            // Token might be expired — try refreshing.
            // AuthService.refreshSession() respects resume grace period internally.
            return from(authService.refreshSession()).pipe(
              switchMap(() => from(authService.getIdToken())),
              switchMap((newToken) => {
                if (!newToken) {
                  // Refresh didn't produce a token.
                  // AuthService handles sign-out internally if appropriate.
                  // If user is still authenticated (resume grace), propagate as error.
                  return throwError(() => error);
                }
                // Retry with new token
                const retryReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${newToken}` },
                });
                return next(retryReq);
              }),
              catchError(() => {
                // Refresh threw — AuthService handled sign-out if appropriate.
                // Propagate the original error.
                return throwError(() => error);
              }),
            );
          }
          return throwError(() => error);
        }),
      );
    }),
  );
};
